import {
  FloatingFocusManager,
  FloatingNode,
  FloatingPortal,
  useFloating,
  useFloatingNodeId,
  useTransitionStyles,
} from '@floating-ui/react'
import React from 'react'
import { IHoverPopover } from '../Tooltip/hooks/useHoverPopper'
import { IPopover } from './hooks/usePopper'

interface PopoverValue<T extends HTMLElement>
  extends Omit<IPopover<T>, 'target' | 'targetProps'>,
    Partial<Pick<IHoverPopover<T>, 'isInstantPhase' | 'delayId'>> {}

export interface IPopoverProps<T extends HTMLElement> extends PopoverValue<T> {
  bodyProps?: React.HTMLAttributes<HTMLDivElement>
  className?: string
  style?: React.CSSProperties
}

export const Popover = <T extends HTMLElement>({
  context,
  isOpen,
  children,
  refs,
  floatingStyles,
  getFloatingProps,
  bodyProps,
  isInstantPhase,
  delayId,
  ...props
}: React.PropsWithChildren<IPopoverProps<T>>) => {
  const nodeId = useFloatingNodeId()
  useFloating({
    nodeId,
  })
  const instantDuration = 0
  const duration = 250
  const { isMounted, styles } = useTransitionStyles(context, {
    duration:
      isInstantPhase && isOpen
        ? {
            open: instantDuration,
            close: delayId === nodeId ? duration : instantDuration,
          }
        : duration,
    initial: {
      opacity: 0,
    },
  })
  if (!isMounted) return null
  return (
    <FloatingNode id={nodeId}>
      <FloatingPortal id={nodeId}>
        <FloatingFocusManager context={context} initialFocus={-1} closeOnFocusOut={false}>
          <div
            className={['popover', props.className].filter(Boolean).join(' ')}
            ref={refs.setFloating}
            style={{ ...props.style, ...floatingStyles, ...styles }}
            {...getFloatingProps()}
          >
            <div
              {...bodyProps}
              className={['popover-body p-0', bodyProps?.className].filter(Boolean).join(' ')}
              style={{
                ...bodyProps?.style,
                position: 'static',
                top: 0,
                left: 0,
                display: 'block',
              }}
            >
              {children}
            </div>
          </div>
        </FloatingFocusManager>
      </FloatingPortal>
    </FloatingNode>
  )
}
Popover.displayName = 'Popover'
